import { InspectionStatus } from '@/shared/enums/inspection-status.enum';
import {
  formatDateToBrazil,
  getLastInspectionStatusKey,
  isInspectionTerminalStatus,
} from '@/shared/utils';
import {
  Avatar,
  Menu,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { CalendarDays, History, SquarePen, User } from 'lucide-react';

type TagColorScheme =
  | 'gray'
  | 'yellow'
  | 'success'
  | 'error'
  | 'blue'
  | 'purple'
  | 'sky';

export interface PolicyInspection {
  id: string;
}

export interface InspectionProposal {
  id: string;
  inspectionId: string;
  proposerName: string;
  proposerDocument: string;
  brokerName: string;
  brokerDocument: string;
  inclusionUserName: string;
  product: string;
  proposalNumber: string;
  createdAt: string;
  transmissionDate: string;
  proposalStatus: string;
  inspectionStatus: string;
  policyInspection: PolicyInspection;
}

const proposalStatusConfig: Record<
  string,
  { label: string; colorScheme: TagColorScheme }
> = {
  '6': { label: 'Proposta em Digitação', colorScheme: 'blue' },
  '7': { label: 'Proposta Aceita', colorScheme: 'success' },
  '8': { label: 'Proposta Transmitida', colorScheme: 'sky' },
  '9': { label: 'Pendente Inspeção', colorScheme: 'yellow' },
  '10': { label: 'Aguardando Emissão', colorScheme: 'success' },
};

export const inspectionStatusConfig: Record<
  InspectionStatus,
  { label: string; colorScheme: TagColorScheme }
> = {
  [InspectionStatus.Dismissed]: { label: 'Dispensada', colorScheme: 'gray' },
  [InspectionStatus.Requested]: { label: 'Solicitada', colorScheme: 'blue' },
  [InspectionStatus.InProgress]: {
    label: 'Em Andamento',
    colorScheme: 'yellow',
  },
  [InspectionStatus.Frustrated]: { label: 'Frustrada', colorScheme: 'error' },
  [InspectionStatus.Canceled]: { label: 'Cancelada', colorScheme: 'error' },
  [InspectionStatus.UnderReview]: {
    label: 'Em Análise',
    colorScheme: 'purple',
  },
  [InspectionStatus.Pending]: { label: 'Pendente', colorScheme: 'yellow' },
  [InspectionStatus.Accepted]: { label: 'Aprovada', colorScheme: 'success' },
  [InspectionStatus.Completed]: { label: 'Completada', colorScheme: 'success' },
};

export type InspectionAction = 'inspection' | 'history';

export function createInspectionsColumns(
  // eslint-disable-next-line no-unused-vars
  onAction: (_action: InspectionAction, _proposal: InspectionProposal) => void
): DataTableColumn<InspectionProposal>[] {
  return [
    {
      accessorKey: 'proposerName',
      header: 'PROPONENTE',
      size: 200,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
              <User className='w-4 h-4 text-white' />
            </Avatar.Root>
            <span className='font-montserrat font-bold text-sm text-alba-black-500'>
              {proposal.proposerName}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'brokerName',
      header: 'CORRETOR',
      size: 200,
      cell: ({ row }) => {
        const proposal = row.original;
        const brokerName = proposal.brokerName
          ? proposal.brokerName.split('\n')[0]
          : '-';

        return (
          <span className='text-alba-black-500 text-sm font-normal font-montserrat'>
            {brokerName}
          </span>
        );
      },
    },
    {
      accessorKey: 'product',
      header: 'PRODUTO',
      size: 160,
      cell: ({ row }) => (
        <span className='text-alba-black-500 text-sm font-normal font-montserrat'>
          {row.original.product}
        </span>
      ),
    },
    {
      accessorKey: 'proposalNumber',
      header: 'Nº DA PROPOSTA',
      size: 160,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <div className='flex flex-col gap-1'>
            <span className='text-alba-black-500 text-sm font-normal font-montserrat'>
              {proposal.proposalNumber}
            </span>
            <div className='flex items-center gap-1 text-alba-gray-600 text-xs'>
              <CalendarDays className='w-4 h-4 text-alba-gray-600' />
              <span>{formatDateToBrazil(proposal.createdAt)}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'transmissionDate',
      header: 'DATA DE TRANSMISSÃO DA PROPOSTA',
      size: 220,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <div className='flex items-center gap-2'>
            <CalendarDays className='w-4 h-4 text-alba-black-500' />
            <span className='text-alba-black-500 text-sm font-normal font-montserrat'>
              {formatDateToBrazil(proposal.transmissionDate)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'proposalStatus',
      header: 'STATUS DA PROPOSTA',
      size: 170,
      cell: ({ row }) => {
        const proposal = row.original;
        const config = proposalStatusConfig[proposal.proposalStatus] ?? {
          label: 'Desconhecido',
          colorScheme: 'gray' as const,
        };

        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      accessorKey: 'inspectionStatus',
      header: 'STATUS DA INSPEÇÃO',
      size: 170,
      cell: ({ row }) => {
        const proposal = row.original;
        const lastStatus = getLastInspectionStatusKey(
          proposal.inspectionStatus
        );
        // `inspectionId` é derivado de `policyInspection?.id`; vazio = a
        // proposta ainda não possui inspeção (policyInspection).
        const hasInspection = Boolean(proposal.inspectionId?.trim());

        let config: { label: string; colorScheme: TagColorScheme };
        if (!hasInspection) {
          // Sem inspeção (policyInspection): o status é sempre "Pendente",
          // independentemente de qualquer `inspectionStatus` que a proposta
          // ainda possa carregar.
          config = inspectionStatusConfig[InspectionStatus.Pending];
        } else if (!lastStatus) {
          // Inspeção existe mas a InsureMO ainda não definiu o status
          // (caso comum em empresariais na fila de triagem): exibimos
          // "Em Triagem", alinhado ao card "Triagem".
          config = { label: 'Em Triagem', colorScheme: 'gray' };
        } else {
          config = inspectionStatusConfig[
            lastStatus.toLowerCase() as InspectionStatus
          ] ?? {
            label: 'Desconhecido',
            colorScheme: 'gray',
          };
        }

        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const proposal = row.original;
        const lastStatus = getLastInspectionStatusKey(
          proposal.inspectionStatus
        );
        const isTerminal = isInspectionTerminalStatus(lastStatus);

        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                {!isTerminal && (
                  <Menu.Item
                    value='inspection'
                    icon={<SquarePen size={24} strokeWidth={1.6} />}
                    onSelect={() => onAction('inspection', proposal)}
                  >
                    Inspeção
                  </Menu.Item>
                )}
                <Menu.Item
                  value='history'
                  icon={<History size={24} strokeWidth={1.6} />}
                  onSelect={() => onAction('history', proposal)}
                >
                  Histórico
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}
